// Seat pricing for a venue and an event's ticket types (same rules as the API).
// - With a seat plan ({ "A-17": ticketTypeId }) the plan alone decides which ticket type
//   sells a seat; seats left out are not on sale.
// - Without one, a ticket type sells the seats of its zone; with a section set, only that
//   section's seats, and a section's own ticket types replace the zone-wide ones there.

// Colors per ticket type, most expensive first. Pink is kept for the selected seat.
const TYPE_STYLES = [
  { seat: "stroke-accent-500 group-hover:fill-accent-100 group-focus-visible:fill-accent-100", fill: "fill-accent-100", text: "fill-accent-700", swatch: "border-accent-500", solid: "bg-accent-500" },
  { seat: "stroke-sky-500 group-hover:fill-sky-100 group-focus-visible:fill-sky-100", fill: "fill-sky-100", text: "fill-sky-700", swatch: "border-sky-500", solid: "bg-sky-500" },
  { seat: "stroke-emerald-500 group-hover:fill-emerald-100 group-focus-visible:fill-emerald-100", fill: "fill-emerald-100", text: "fill-emerald-700", swatch: "border-emerald-500", solid: "bg-emerald-500" },
  { seat: "stroke-amber-500 group-hover:fill-amber-100 group-focus-visible:fill-amber-100", fill: "fill-amber-100", text: "fill-amber-700", swatch: "border-amber-500", solid: "bg-amber-500" },
  { seat: "stroke-rose-500 group-hover:fill-rose-100 group-focus-visible:fill-rose-100", fill: "fill-rose-100", text: "fill-rose-700", swatch: "border-rose-500", solid: "bg-rose-500" },
  { seat: "stroke-teal-600 group-hover:fill-teal-100 group-focus-visible:fill-teal-100", fill: "fill-teal-100", text: "fill-teal-700", swatch: "border-teal-600", solid: "bg-teal-600" },
];

export const seatPlanKey = (zoneCode, seatNumber) => `${zoneCode}-${seatNumber}`;

// { [ticketTypeId]: style }, stable for an event: by price (highest first), then name
export const ticketTypeStyles = (ticketTypes = []) => {
  const sorted = [...ticketTypes].sort(
    (a, b) => (b.price || 0) - (a.price || 0) || (a.name?.az || "").localeCompare(b.name?.az || "")
  );
  return Object.fromEntries(sorted.map((type, i) => [type._id, TYPE_STYLES[i % TYPE_STYLES.length]]));
};

// Zone/section rule, used when the event has no seat plan
export const ticketTypesForSeat = (ticketTypes = [], zoneCode, sectionCode) => {
  const zoneTypes = ticketTypes.filter((type) => type.zone === zoneCode);
  const sectionTypes = sectionCode ? zoneTypes.filter((type) => type.section === sectionCode) : [];
  return sectionTypes.length ? sectionTypes : zoneTypes.filter((type) => !type.section);
};

// "40₼", or "40₼-dan" when the seat can be bought at several prices
export const priceLabel = (types) => {
  if (!types.length) return undefined;
  const prices = types.map((type) => type.price || 0);
  const minPrice = Math.min(...prices);
  return prices.some((price) => price !== minPrice) ? `${minPrice}₼-dan` : `${minPrice}₼`;
};

export const buildSeatPricing = (venue, ticketTypes = [], seatPlan) => {
  const typesById = Object.fromEntries(ticketTypes.map((type) => [type._id, type]));
  const styles = ticketTypeStyles(ticketTypes);

  const typesForSeat = (zoneCode, seat) => {
    if (!seat) return [];
    if (seatPlan) {
      const type = typesById[seatPlan[seatPlanKey(zoneCode, seat.number)]];
      return type ? [type] : [];
    }
    return ticketTypesForSeat(ticketTypes, zoneCode, seat.section);
  };

  // { price, style } for seats on sale; the cheapest ticket type gives the color
  const seatInfo = (zoneCode, seat) => {
    const types = typesForSeat(zoneCode, seat);
    if (!types.length) return undefined;
    const cheapest = types.reduce((min, type) => ((type.price || 0) < (min.price || 0) ? type : min));
    return { price: priceLabel(types), style: styles[cheapest._id] };
  };

  // Ticket types that sell at least one seat, highest price first
  const usedTypeIds = new Set();
  for (const zone of venue?.zones || []) {
    for (const seat of zone.seats || []) {
      typesForSeat(zone.code, seat).forEach((type) => usedTypeIds.add(type._id));
    }
  }
  const priceList = ticketTypes
    .filter((type) => usedTypeIds.has(type._id))
    .sort((a, b) => (b.price || 0) - (a.price || 0))
    .map((type) => ({ key: type._id, label: type.name?.az, price: `${type.price || 0}₼`, style: styles[type._id] }));

  return { typesForSeat, seatInfo, priceList, styles };
};

// Plan equal to the zone/section ticket type settings, for events without a seat plan yet
export const planFromTicketTypeSettings = (venue, ticketTypes = []) => {
  const plan = {};
  for (const zone of venue?.zones || []) {
    for (const seat of zone.seats || []) {
      const types = ticketTypesForSeat(ticketTypes, zone.code, seat.section);
      if (types.length) {
        const cheapest = types.reduce((min, type) => ((type.price || 0) < (min.price || 0) ? type : min));
        plan[seatPlanKey(zone.code, seat.number)] = cheapest._id;
      }
    }
  }
  return plan;
};

// The event's current plan: its saved seat plan (only entries of known ticket types),
// or the plan equal to its zone/section settings
export const currentSeatPlan = (venue, ticketTypes = [], seatPlan) => {
  if (!seatPlan) return planFromTicketTypeSettings(venue, ticketTypes);
  const known = new Set(ticketTypes.map((type) => type._id));
  return Object.fromEntries(Object.entries(seatPlan).filter(([, typeId]) => known.has(typeId)));
};

// "1-16, 20, 25-30" -> { numbers: [1, ..., 16, 20, 25, ..., 30] } or { error }
export const parseSeatNumbers = (text = "") => {
  const numbers = new Set();
  const parts = text.split(",").map((part) => part.trim()).filter(Boolean);
  for (const part of parts) {
    const match = /^(\d+)\s*-\s*(\d+)$/.exec(part) || /^(\d+)$/.exec(part);
    if (!match) return { error: `"${part}" başa düşülmədi` };
    const from = Number(match[1]);
    const to = Number(match[2] ?? match[1]);
    if (from < 1 || from > to || to - from > 2000) return { error: `"${part}" aralığı səhvdir` };
    for (let number = from; number <= to; number++) numbers.add(number);
  }
  return { numbers: [...numbers].sort((a, b) => a - b) };
};

// [1, 2, 3, 5] -> "1-3, 5"
export const formatSeatNumbers = (numbers = []) => {
  const sorted = [...new Set(numbers)].sort((a, b) => a - b);
  const ranges = [];
  for (const number of sorted) {
    const last = ranges[ranges.length - 1];
    if (last && number === last[1] + 1) last[1] = number;
    else ranges.push([number, number]);
  }
  return ranges.map(([from, to]) => (from === to ? `${from}` : `${from}-${to}`)).join(", ");
};
